import { useState } from 'react';
import { IconCheck, IconShieldLock, IconUsers, IconX } from '@tabler/icons-react';
import {
  Accordion,
  Avatar,
  List,
  Metric,
  Panel,
  cn,
  PRESS,
  formatDateTime,
  formatPercentage,
  type AccordionItemData,
  type ListItemData,
} from 'escrowbuild-ui';

import {
  useFixtureVocabulary,
  usePublications,
  useT,
  type AppTranslationKey,
} from '@/i18n';
import {
  ACCESS_LOG,
  DOCUMENTS,
  PEOPLE,
  PROJECTS,
  findInvestor,
} from '@/data/domain';

const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';

const PROJECT_NAME = new Map(PROJECTS.map((project) => [project.id, project.name]));

/* Yes and No are the only two cells the screen reasons about — the counters
   above the matrix test for them and so does the cell's own treatment — so the
   two keys are named once rather than spelled out at each of the three. */
const YES: AppTranslationKey = 'permissions.cell.yes';
const NO: AppTranslationKey = 'permissions.cell.no';

const MATRIX_COLUMNS: AppTranslationKey[] = [
  'permissions.col.profile',
  'permissions.col.projects',
  'permissions.col.field',
  'permissions.col.financial',
  'permissions.col.publishing',
  'permissions.col.portal',
];

const ACCESS_COLUMNS: AppTranslationKey[] = [
  'permissions.access.investor',
  'permissions.access.development',
  'permissions.access.update',
  'permissions.access.viewed',
  'permissions.access.read',
];

/**
 * The five profiles of §14, reproduced cell for cell from the discovery
 * document. `can` and `cannot` restate the same cells in the second person so a
 * client can answer "what can this person actually do?" without reading a matrix
 * column across.
 */
interface Profile {
  id: string;
  name: AppTranslationKey;
  summary: AppTranslationKey;
  projects: AppTranslationKey;
  field: AppTranslationKey;
  financial: AppTranslationKey;
  publishing: AppTranslationKey;
  portal: AppTranslationKey;
  can: AppTranslationKey[];
  cannot: AppTranslationKey[];
}

const PROFILES: Profile[] = [
  {
    id: 'field-lead',
    name: 'permissions.profile.fieldLead',
    summary: 'permissions.profile.fieldLeadSummary',
    projects: 'permissions.cell.onlyAssignedProjects',
    field: 'permissions.cell.createMedia',
    financial: NO,
    publishing: NO,
    portal: NO,
    can: ['permissions.fieldLead.can1', 'permissions.fieldLead.can2'],
    cannot: [
      'permissions.fieldLead.cannot1',
      'permissions.fieldLead.cannot2',
      'permissions.fieldLead.cannot3',
      'permissions.fieldLead.cannot4',
    ],
  },
  {
    id: 'manager',
    name: 'permissions.profile.manager',
    summary: 'permissions.profile.managerSummary',
    projects: 'permissions.cell.managedProjects',
    field: 'permissions.cell.consultConsolidate',
    financial: 'permissions.cell.perAuthority',
    publishing: 'permissions.cell.mayPrepare',
    portal: NO,
    can: [
      'permissions.manager.can1',
      'permissions.manager.can2',
      'permissions.manager.can3',
    ],
    cannot: [
      'permissions.manager.cannot1',
      'permissions.manager.cannot2',
      'permissions.manager.cannot3',
    ],
  },
  {
    id: 'finance',
    name: 'permissions.profile.finance',
    summary: 'permissions.profile.financeSummary',
    projects: 'permissions.cell.authorisedProjects',
    field: 'permissions.cell.consultWhenNeeded',
    financial: 'permissions.cell.createConsultVerify',
    publishing: 'permissions.cell.notNecessarily',
    portal: NO,
    can: ['permissions.finance.can1', 'permissions.finance.can2'],
    cannot: [
      'permissions.finance.cannot1',
      'permissions.finance.cannot2',
      'permissions.finance.cannot3',
    ],
  },
  {
    id: 'owner',
    name: 'permissions.profile.owner',
    summary: 'permissions.profile.ownerSummary',
    projects: 'common.all',
    field: 'permissions.cell.broad',
    financial: 'permissions.cell.broad',
    publishing: YES,
    portal: NO,
    can: ['permissions.owner.can1', 'permissions.owner.can2', 'permissions.owner.can3'],
    cannot: ['permissions.owner.cannot1'],
  },
  {
    id: 'investor',
    name: 'shell.portal.role',
    summary: 'permissions.profile.investorSummary',
    projects: 'permissions.cell.onlyTheirParticipations',
    field: 'permissions.cell.publishedContentOnly',
    financial: 'permissions.cell.releasedFinancialsOnly',
    publishing: NO,
    portal: YES,
    can: ['permissions.investor.can1', 'permissions.investor.can2', 'permissions.investor.can3'],
    cannot: [
      'permissions.investor.cannot1',
      'permissions.investor.cannot2',
      'permissions.investor.cannot3',
    ],
  },
];

/**
 * Which of the team fixture's job titles belongs to which profile. The fixture
 * names jobs ("Site lead", "Approver"); §14 names profiles. The mapping lives
 * here rather than in the fixture because it is a permission decision, not a
 * property of the person.
 */
const PROFILE_OF_JOB: Record<string, string> = {
  'Site lead': 'field-lead',
  Field: 'field-lead',
  Coordinator: 'manager',
  Approver: 'manager',
  Finance: 'finance',
  'Back office': 'finance',
  Investor: 'investor',
};

function holdersOf(profileId: string) {
  return PEOPLE.filter((person) => PROFILE_OF_JOB[person.role] === profileId);
}

const RULES: { id: string; header: AppTranslationKey; body: AppTranslationKey }[] = [
  {
    id: 'by-project',
    header: 'permissions.rule.byProject',
    body: 'permissions.rule.byProjectBody',
  },
  {
    id: 'by-role',
    header: 'permissions.rule.byRole',
    body: 'permissions.rule.byRoleBody',
  },
  {
    id: 'publication',
    header: 'permissions.rule.publication',
    body: 'permissions.rule.publicationBody',
  },
  {
    id: 'investor',
    header: 'permissions.rule.investor',
    body: 'permissions.rule.investorBody',
  },
  {
    id: 'traceability',
    header: 'permissions.rule.traceability',
    body: 'permissions.rule.traceabilityBody',
  },
  {
    id: 'verification',
    header: 'permissions.rule.verification',
    body: 'permissions.rule.verificationBody',
  },
];

export function PermissionsPage() {
  const t = useT();
  /* The team fixture's job titles, in the reader's language: a person's name is a
     proper noun, the role under it is a word. */
  const vocabulary = useFixtureVocabulary();
  const [selectedId, setSelectedId] = useState(PROFILES[0]?.id ?? '');
  const [openRules, setOpenRules] = useState<string[]>(['by-project']);

  const selected = PROFILES.find((profile) => profile.id === selectedId) ?? PROFILES[0];
  const mapped = PEOPLE.filter((person) => PROFILE_OF_JOB[person.role]).length;
  const mayRelease = PROFILES.filter((profile) => profile.publishing === YES).length;
  const withPortal = PROFILES.filter((profile) => profile.portal === YES).length;
  const unreleased = DOCUMENTS.filter((document) => document.status === 'verified' && !document.published).length;

  const holders: ListItemData[] = (selected ? holdersOf(selected.id) : []).map((person) => ({
    id: person.name,
    title: person.name,
    /* The name is a proper noun and stays; the job title under it is a word. */
    subtitle: vocabulary.personRole(person.role),
    avatar: { name: person.name },
  }));

  const rules: AccordionItemData[] = RULES.map((rule) => ({
    id: rule.id,
    header: t(rule.header),
    content: (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-body">{t(rule.body)}</p>
        {rule.id === 'traceability' ? <AccessLogTable /> : null}
        {rule.id === 'verification' ? (
          <p className="text-xs text-muted">
            {t('permissions.verificationUnreleased', { count: unreleased })} ·{' '}
            {t('permissions.verificationReleased', {
              count: DOCUMENTS.length - unreleased,
            })}
          </p>
        ) : null}
      </div>
    ),
  }));

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('permissions.title')}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted">{t('permissions.intro')}</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label={t('permissions.metric.profiles')} value={PROFILES.length} format="number" />
        <Metric
          label={t('permissions.metric.mapped')}
          value={mapped}
          format="number"
          hint={t('permissions.metric.mappedHint', { count: PEOPLE.length })}
        />
        <Metric
          label={t('permissions.metric.mayRelease')}
          value={mayRelease}
          format="number"
          tone="info"
          hint={t('permissions.metric.mayReleaseHint')}
        />
        <Metric
          label={t('permissions.metric.portal')}
          value={withPortal}
          format="number"
          tone="info"
          hint={t('permissions.metric.portalHint')}
        />
      </div>

      <Panel
        title={t('permissions.matrixTitle')}
        description={t('permissions.matrixHint')}
        padding="none"
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{t('permissions.matrixCaption')}</caption>
            <thead>
              <tr className="border-b border-line">
                {MATRIX_COLUMNS.map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="px-4 py-3 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t(heading)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PROFILES.map((profile) => {
                const isSelected = profile.id === selected?.id;
                return (
                  <tr
                    key={profile.id}
                    className={cn(
                      'border-b border-line-subtle align-top last:border-b-0',
                      isSelected && 'bg-primary-subtle',
                    )}
                  >
                    <th scope="row" className="px-4 py-3 text-left">
                      <button
                        type="button"
                        onClick={() => setSelectedId(profile.id)}
                        aria-current={isSelected ? 'true' : undefined}
                        className={cn(
                          'rounded-xs text-left text-sm font-medium',
                          // `text-primary` on the selected row would sit on that
                          // row's `bg-primary-subtle`, which measures 4.29:1 —
                          // under AA for 13px text. The selection is already
                          // carried by the row tint and `aria-current`, so the
                          // label stays in `text-heading` (13.42:1 on the tint)
                          // and the state loses no cue.
                          'text-heading',
                          !isSelected && 'hover:text-primary',
                          PRESS,
                          FOCUS,
                        )}
                      >
                        {t(profile.name)}
                      </button>
                    </th>
                    <td className="px-4 py-3">
                      <MatrixCell value={profile.projects} />
                    </td>
                    <td className="px-4 py-3">
                      <MatrixCell value={profile.field} />
                    </td>
                    <td className="px-4 py-3">
                      <MatrixCell value={profile.financial} />
                    </td>
                    <td className="px-4 py-3">
                      <MatrixCell value={profile.publishing} />
                    </td>
                    <td className="px-4 py-3">
                      <MatrixCell value={profile.portal} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="border-t border-line-subtle px-4 py-3 text-xs text-muted">
          {t('permissions.matrixNote')}
        </p>
      </Panel>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel
          title={
            <span className="flex items-center gap-2">
              <IconShieldLock size={16} aria-hidden="true" />
              {selected ? t(selected.name) : t('permissions.col.profile')}
            </span>
          }
          description={selected ? t(selected.summary) : undefined}
          padding="lg"
        >
          {selected ? (
            <div className="flex flex-col gap-5">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <PermissionRow term={t('permissions.col.projects')} value={selected.projects} />
                <PermissionRow term={t('permissions.col.field')} value={selected.field} />
                <PermissionRow term={t('permissions.col.financial')} value={selected.financial} />
                <PermissionRow term={t('permissions.col.publishing')} value={selected.publishing} />
                <PermissionRow term={t('permissions.col.portal')} value={selected.portal} />
              </dl>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <h3 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                    {t('permissions.can')}
                  </h3>
                  <ul className="mt-2 flex list-none flex-col gap-1.5 p-0">
                    {selected.can.map((entry) => (
                      <li key={entry} className="flex gap-2 text-xs text-body">
                        <IconCheck size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-success" />
                        <span>{t(entry)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                    {t('permissions.cannot')}
                  </h3>
                  <ul className="mt-2 flex list-none flex-col gap-1.5 p-0">
                    {selected.cannot.map((entry) => (
                      <li key={entry} className="flex gap-2 text-xs text-body">
                        <IconX size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-muted" />
                        <span>{t(entry)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="flex items-center gap-2 font-display text-2xs font-semibold uppercase tracking-label text-muted">
                  <IconUsers size={14} aria-hidden="true" />
                  {t('permissions.whoHolds')}
                </h3>
                {holders.length > 0 ? (
                  <List
                    items={holders}
                    size="sm"
                    divided
                    label={t('permissions.holdersLabel', { name: t(selected.name) })}
                  />
                ) : (
                  <p className="mt-2 text-xs text-muted">{t('permissions.noHolders')}</p>
                )}
              </div>

              {selected.id === 'investor' ? (
                <p className="text-xs text-muted">{t('permissions.portalNote')}</p>
              ) : null}
            </div>
          ) : null}
        </Panel>

        <Panel
          title={t('permissions.rulesTitle')}
          description={t('permissions.rulesHint')}
          padding="lg"
        >
          <Accordion
            items={rules}
            type="multiple"
            variant="separated"
            headingLevel={3}
            open={openRules}
            onOpenChange={setOpenRules}
          />
        </Panel>
      </div>
    </div>
  );
}

function PermissionRow({ term, value }: { term: string; value: AppTranslationKey }) {
  return (
    <div>
      <dt className="font-display text-2xs font-semibold uppercase tracking-label text-muted">{term}</dt>
      <dd className="mt-1 text-sm text-body">
        <MatrixCell value={value} />
      </dd>
    </div>
  );
}

/** Yes and No carry their own weight; a conditional phrase is left as plain prose. */
function MatrixCell({ value }: { value: AppTranslationKey }) {
  const t = useT();
  if (value === YES) return <span className="font-medium text-success">{t(value)}</span>;
  if (value === NO) return <span className="text-muted">{t(value)}</span>;
  return <span>{t(value)}</span>;
}

/** §14's traceability rule, shown with the platform's own access log. */
function AccessLogTable() {
  const t = useT();
  /* Each row names the update that was opened, in the reader's language. */
  const publications = usePublications();
  const rows = [...ACCESS_LOG].sort((a, b) => b.viewedAt.localeCompare(a.viewedAt));

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <caption className="sr-only">{t('permissions.accessLogCaption')}</caption>
        <thead>
          <tr className="border-b border-line">
            {ACCESS_COLUMNS.map((heading) => (
              <th
                key={heading}
                scope="col"
                className="px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
              >
                {t(heading)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((entry) => (
            <tr key={entry.id} className="border-b border-line-subtle last:border-b-0">
              <th scope="row" className="px-3 py-2 text-left font-normal">
                <span className="flex items-center gap-2">
                  <Avatar name={findInvestor(entry.investorId)?.name ?? entry.investorId} size="xs" />
                  {findInvestor(entry.investorId)?.name ?? entry.investorId}
                </span>
              </th>
              <td className="px-3 py-2 text-body">{PROJECT_NAME.get(entry.projectId) ?? entry.projectId}</td>
              <td className="px-3 py-2 text-body">
                {publications.find((publication) => publication.id === entry.publicationId)?.title ??
                  entry.publicationId}
              </td>
              <td className="px-3 py-2 text-body">{formatDateTime(entry.viewedAt)}</td>
              <td className="px-3 py-2 text-body">{formatPercentage(entry.depth, { precision: 0 })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
